/**
 * Client-side draft helpers for the prompt editor.
 *
 * ── On the variable extractor below ─────────────────────────────────────────
 *
 * The backend, the Node SDK and the Python SDK share ONE template engine, pinned
 * byte-for-byte by backend/services/prompts/template.vectors.json. The browser
 * bundle can't import from backend/ (outside CRA's src/), so rather than add a
 * fourth copy that would inevitably drift, `detectNames` here is deliberately a
 * **UX hint only**:
 *
 *   - It powers the "variables detected as you type" affordance.
 *   - It is NOT the contract. On save, the server runs the real engine
 *     (reconcileVariables) and returns the authoritative declarations, which the
 *     editor then adopts wholesale.
 *
 * So if this ever disagrees with the real parser, the disagreement is visible
 * for one keystroke and corrected on save — it can never ship a wrong prompt.
 * Anything that must be exact (rendering, strictness, sections) is server-side.
 * Keep it that way: do not grow this into a renderer.
 */

// Capture the sigil (group 1) so a section head {{#x}}/{{^x}} can be inferred as
// `json`, matching the server's extractVariables. Group 2 is the name.
const HINT_RE = /\{\{\s*([#^/!&>]?)\s*([A-Za-z_$][A-Za-z0-9_$.]*)\s*\}\}/g;

export const ROLES = ['system', 'user', 'assistant'];

export const VAR_TYPES = ['string', 'number', 'boolean', 'json', 'messages'];

/** Root variable names referenced anywhere in the draft. Hint only — see above.
 *  Mirrors the server's type inference: placeholder → 'messages', a name used as
 *  a section head {{#x}}/{{^x}} → 'json', everything else → 'string'. */
export function detectNames(messages) {
  const order = [];
  const seen = new Set();
  const sections = new Set();     // names used as {{#x}} / {{^x}}
  const fixed = new Map();        // placeholder names → 'messages'
  const note = (n) => { if (!seen.has(n)) { seen.add(n); order.push(n); } };

  for (const node of messages || []) {
    if (!node) continue;
    if (node.type === 'placeholder') {
      if (node.name) { note(node.name); fixed.set(node.name, 'messages'); }
      continue;
    }
    for (const block of node.content || []) {
      if (block?.type !== 'text' || typeof block.text !== 'string') continue;
      HINT_RE.lastIndex = 0;
      let m;
      // eslint-disable-next-line no-cond-assign
      while ((m = HINT_RE.exec(block.text)) !== null) {
        const sigil = m[1];
        if (sigil === '/' || sigil === '!') continue;   // close tag / comment: not a variable
        const root = m[2].split('.')[0];
        note(root);
        if (sigil === '#' || sigil === '^') sections.add(root);
      }
    }
  }

  return order.map((name) => ({
    name,
    type: fixed.get(name) || (sections.has(name) ? 'json' : 'string'),
  }));
}

/**
 * Names used ONLY inside {{#section}}…{{/section}} bodies — per-item fields,
 * not call-time arguments.
 *
 * The server deliberately still DECLARES these (see extractNames), and that
 * declaration is load-bearing: it seeds the name into the top-level render
 * scope, which is (a) what lets an item that omits the field render empty
 * instead of throwing under strict rendering, and (b) what makes a Default on
 * it act as the per-item fallback. So compile behaviour must not change.
 *
 * What WAS wrong is how three surfaces presented them: the "Use this prompt"
 * snippet told developers to pass them, the Variables panel showed them as
 * ordinary variables, and the playground offered inputs for them. This
 * detector exists so those surfaces can tell the truth instead. Hint-only,
 * same contract as detectNames above.
 *
 * A name also used OUTSIDE any section (or as a section head, or a
 * placeholder) is not inner — the caller genuinely can pass it.
 */
export function sectionInnerNames(messages) {
  const insideOnly = new Set();
  const outside = new Set();      // seen at depth 0, or as a head/placeholder

  for (const node of messages || []) {
    if (!node) continue;
    if (node.type === 'placeholder') { if (node.name) outside.add(node.name); continue; }
    for (const block of node.content || []) {
      if (block?.type !== 'text' || typeof block.text !== 'string') continue;
      HINT_RE.lastIndex = 0;
      let depth = 0;
      let m;
      // eslint-disable-next-line no-cond-assign
      while ((m = HINT_RE.exec(block.text)) !== null) {
        const sigil = m[1];
        const root = m[2].split('.')[0];
        if (sigil === '!') continue;
        if (sigil === '#' || sigil === '^') { outside.add(root); depth += 1; continue; }
        if (sigil === '/') { depth = Math.max(0, depth - 1); continue; }
        if (depth > 0) insideOnly.add(root); else outside.add(root);
      }
    }
  }
  return new Set([...insideOnly].filter((n) => !outside.has(n)));
}

/**
 * Merge detected names with the user's declarations. The user's edits win on
 * every field; detection only adds names they haven't declared and drops
 * declarations no longer referenced anywhere. Mirrors the server's
 * reconcileVariables so the panel doesn't jump around on save.
 */
export function reconcileVariables(messages, declared) {
  const found = detectNames(messages);
  const byName = new Map((declared || []).filter((v) => v?.name).map((v) => [v.name, v]));

  return found.map((f) => {
    const d = byName.get(f.name);
    if (!d) return { name: f.name, type: f.type };
    // Structural types (messages placeholder, json section) follow usage, not a
    // stale declaration — force them; keep the user's other fields.
    return (f.type === 'messages' || f.type === 'json')
      ? { ...d, name: f.name, type: f.type }
      : { ...d, name: f.name };
  });
}

export const textBlock = (text = '') => ({ type: 'text', text });

export const newMessage = (role = 'user') => ({ role, content: [textBlock('')] });

export const newPlaceholder = (name = 'chat_history') => ({ type: 'placeholder', name });

/** The text of a message, for the single-textarea-per-message editor. */
export const messageText = (m) =>
  (m.content || []).filter((b) => b.type === 'text').map((b) => b.text).join('');

export const setMessageText = (m, text) => ({ ...m, content: [textBlock(text)] });

/** An empty draft for a prompt with no versions yet. Starts with a single
 *  system message; the author adds user/assistant turns from the toolbar. No
 *  model default: provider and model are left unset until the author chooses. */
export const emptyDraft = () => ({
  messages: [
    { role: 'system', content: [textBlock('')] },
  ],
  model: {},
  tools: [],
  response_format: null,
  variables: [],
});

